import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  cameraOnHome,
  clampCamera,
  integratePan,
  panCamera,
  stepInertia,
  wheelPanDeltas,
  worldView,
  zoomCamera,
  type Bounds,
  type Camera,
  type Vec2,
} from "./camera";
import {
  applyPinch,
  clampPad,
  dragThreshold,
  isHistoryGestureEdge,
  pinchOf,
  scaleLimits,
  viewCullMargin,
} from "./gestures";
import type { WallLayout } from "./wall-layout";

type ViewState = Bounds & { scale: number; vw: number; vh: number };

export function useWallCamera(
  layout: WallLayout,
  reducedMotion: boolean,
  enabled: boolean,
) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const camRef = useRef<Camera>({ x: 0, y: 0, scale: 1 });
  const velRef = useRef<Vec2>({ x: 0, y: 0 });
  const dragRef = useRef(false);
  const bootedRef = useRef(false);
  const interactedRef = useRef(false);
  const pointersRef = useRef(new Map<number, Vec2>());
  const lastRef = useRef<Vec2>({ x: 0, y: 0 });
  const lastTRef = useRef(0);
  const pinchRef = useRef({ dist: 0, mid: { x: 0, y: 0 } });
  const movedRef = useRef(0);
  const thresholdRef = useRef(dragThreshold("mouse"));
  const rafRef = useRef(0);
  const coastRafRef = useRef(0);
  const lastCoastRef = useRef(0);
  const reducedRef = useRef(reducedMotion);
  const enabledRef = useRef(enabled);
  const layoutRef = useRef(layout);
  const layoutKeyRef = useRef<string | null>(null);

  reducedRef.current = reducedMotion;
  enabledRef.current = enabled;
  layoutRef.current = layout;

  const [dragging, setDragging] = useState(false);
  const [view, setView] = useState<ViewState>({
    x: 0,
    y: 0,
    w: 1920,
    h: 1080,
    scale: 1,
    vw: 1920,
    vh: 1080,
  });

  const measure = useCallback((): { width: number; height: number } => {
    const el = viewportRef.current;
    if (!el) return { width: 1280, height: 800 };
    const r = el.getBoundingClientRect();
    return { width: Math.max(1, r.width), height: Math.max(1, r.height) };
  }, []);

  const publish = useCallback(() => {
    const vp = measure();
    const rect = worldView(camRef.current, vp, viewCullMargin(vp));
    setView({ ...rect, scale: camRef.current.scale, vw: vp.width, vh: vp.height });
  }, [measure]);

  const applyTransform = useCallback(() => {
    const el = worldRef.current;
    if (!el) return;
    const { x, y, scale } = camRef.current;
    el.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
  }, []);

  const stopCoast = useCallback(() => {
    if (coastRafRef.current) {
      cancelAnimationFrame(coastRafRef.current);
      coastRafRef.current = 0;
    }
    velRef.current = { x: 0, y: 0 };
  }, []);

  const startCoast = useCallback(() => {
    if (reducedRef.current) {
      velRef.current = { x: 0, y: 0 };
      return;
    }
    if (coastRafRef.current) return;
    lastCoastRef.current = performance.now();
    const step = (now: number) => {
      const dt = Math.min(32, now - lastCoastRef.current);
      lastCoastRef.current = now;
      velRef.current = stepInertia(velRef.current, dt);
      if (velRef.current.x === 0 && velRef.current.y === 0) {
        coastRafRef.current = 0;
        publish();
        return;
      }
      const vp = measure();
      const limits = scaleLimits(vp);
      const coasted = integratePan(camRef.current, velRef.current, dt);
      camRef.current = clampCamera(
        zoomCamera(coasted, 1, vp.width / 2, vp.height / 2, limits.min, limits.max),
        layoutRef.current,
        vp,
        clampPad(vp),
      );
      applyTransform();
      coastRafRef.current = requestAnimationFrame(step);
    };
    coastRafRef.current = requestAnimationFrame(step);
  }, [applyTransform, measure, publish]);

  const commit = useCallback(
    (next: Camera) => {
      const vp = measure();
      const limits = scaleLimits(vp);
      const scaled = zoomCamera(
        next,
        1,
        vp.width / 2,
        vp.height / 2,
        limits.min,
        limits.max,
      );
      camRef.current = clampCamera(scaled, layoutRef.current, vp, clampPad(vp));
      applyTransform();
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        publish();
      });
    },
    [applyTransform, measure, publish],
  );

  const reset = useCallback(() => {
    stopCoast();
    interactedRef.current = false;
    const home = cameraOnHome(layoutRef.current.home, measure());
    commit(home);
  }, [commit, measure, stopCoast]);

  const layoutKey = layout.tiles.map((tile) => tile.key).join("\n");

  useLayoutEffect(() => {
    const vp = measure();
    const orderChanged =
      layoutKeyRef.current !== null && layoutKeyRef.current !== layoutKey;
    layoutKeyRef.current = layoutKey;
    if (orderChanged) interactedRef.current = false;
    if (!bootedRef.current || orderChanged || !interactedRef.current) {
      camRef.current = cameraOnHome(layout.home, vp);
      bootedRef.current = true;
    } else {
      camRef.current = clampCamera(camRef.current, layout, vp, clampPad(vp));
    }
    applyTransform();
    publish();
  }, [layout, layoutKey, applyTransform, measure, publish]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const originOf = (clientX: number, clientY: number) => {
      const r = el.getBoundingClientRect();
      return { x: clientX - r.left, y: clientY - r.top };
    };

    const onPointerDown = (event: PointerEvent) => {
      if (!enabledRef.current) return;
      if (event.button !== 0 && event.pointerType === "mouse") return;
      el.setPointerCapture(event.pointerId);
      pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      stopCoast();
      movedRef.current = 0;
      dragRef.current = false;
      thresholdRef.current = dragThreshold(event.pointerType);
      lastRef.current = { x: event.clientX, y: event.clientY };
      lastTRef.current = performance.now();
      if (pointersRef.current.size === 2) {
        const pts = [...pointersRef.current.values()];
        const a = pts[0];
        const b = pts[1];
        if (a && b) pinchRef.current = pinchOf(a, b);
      }
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!pointersRef.current.has(event.pointerId)) return;
      pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (pointersRef.current.size >= 2) {
        const pts = [...pointersRef.current.values()];
        const a = pts[0];
        const b = pts[1];
        if (!a || !b) return;
        const nextPinch = pinchOf(a, b);
        const prev = pinchRef.current;
        const origin = originOf(nextPinch.mid.x, nextPinch.mid.y);
        const limits = scaleLimits(measure());
        const next = applyPinch(
          camRef.current,
          prev,
          nextPinch,
          origin,
          limits.min,
          limits.max,
        );
        pinchRef.current = nextPinch;
        dragRef.current = true;
        movedRef.current += 12;
        interactedRef.current = true;
        setDragging(true);
        commit(next);
        return;
      }

      const now = performance.now();
      const dx = event.clientX - lastRef.current.x;
      const dy = event.clientY - lastRef.current.y;
      const dt = Math.max(1, now - lastTRef.current);
      lastRef.current = { x: event.clientX, y: event.clientY };
      lastTRef.current = now;
      movedRef.current += Math.hypot(dx, dy);
      if (movedRef.current > thresholdRef.current) {
        if (!dragRef.current) {
          dragRef.current = true;
          setDragging(true);
        }
        velRef.current = { x: dx / dt, y: dy / dt };
        interactedRef.current = true;
        commit(panCamera(camRef.current, dx, dy));
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      if (!pointersRef.current.has(event.pointerId)) return;
      pointersRef.current.delete(event.pointerId);
      if (pointersRef.current.size === 1) {
        const leftover = [...pointersRef.current.values()][0];
        if (leftover) lastRef.current = leftover;
        lastTRef.current = performance.now();
      }
      if (pointersRef.current.size === 0) {
        const wasDrag = dragRef.current;
        setDragging(false);
        if (wasDrag && !reducedRef.current) startCoast();
        // Pointer capture retargets click to the viewport, so a tile tap is
        // completed here. After a pan, dragRef stays true and blocks the
        // delayed click some mobile browsers fire on the original target.
        if (!wasDrag) {
          dragRef.current = false;
          const hit = document.elementFromPoint(event.clientX, event.clientY);
          const tile = hit instanceof Element ? hit.closest("button.tile.app") : null;
          if (tile instanceof HTMLButtonElement) tile.click();
        }
      }
    };

    const onWheel = (event: WheelEvent) => {
      if (!enabledRef.current) return;
      event.preventDefault();
      stopCoast();
      interactedRef.current = true;
      const origin = originOf(event.clientX, event.clientY);
      const vp = measure();
      const limits = scaleLimits(vp);
      const deltas = wheelPanDeltas(event, 16, vp.height);
      if (event.ctrlKey || event.metaKey) {
        const factor = Math.exp(-deltas.y * 0.0018);
        commit(zoomCamera(camRef.current, factor, origin.x, origin.y, limits.min, limits.max));
        return;
      }
      commit(panCamera(camRef.current, -deltas.x, -deltas.y));
    };

    const onDblClick = (event: MouseEvent) => {
      if (!enabledRef.current) return;
      interactedRef.current = true;
      const origin = originOf(event.clientX, event.clientY);
      const limits = scaleLimits(measure());
      commit(zoomCamera(camRef.current, 1.28, origin.x, origin.y, limits.min, limits.max));
    };

    const onTouchStart = (event: TouchEvent) => {
      if (!enabledRef.current) return;
      if (event.touches.length > 1) {
        event.preventDefault();
        return;
      }
      const t = event.touches[0];
      if (!t) return;
      const r = el.getBoundingClientRect();
      if (isHistoryGestureEdge(t.clientX - r.left, r.width)) {
        event.preventDefault();
      }
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!enabledRef.current) return;
      if (pointersRef.current.size === 0) return;
      event.preventDefault();
    };

    const onGesture = (event: Event) => {
      if (!enabledRef.current) return;
      event.preventDefault();
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("dblclick", onDblClick);
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("gesturestart", onGesture);
    el.addEventListener("gesturechange", onGesture);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("dblclick", onDblClick);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("gesturestart", onGesture);
      el.removeEventListener("gesturechange", onGesture);
    };
  }, [commit, measure, startCoast, stopCoast]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!enabledRef.current) return;
      const t = event.target;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) return;
      const step = event.shiftKey ? 220 : 96;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        stopCoast();
        interactedRef.current = true;
        commit(panCamera(camRef.current, step, 0));
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        stopCoast();
        interactedRef.current = true;
        commit(panCamera(camRef.current, -step, 0));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        stopCoast();
        interactedRef.current = true;
        commit(panCamera(camRef.current, 0, step));
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        stopCoast();
        interactedRef.current = true;
        commit(panCamera(camRef.current, 0, -step));
      } else if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        interactedRef.current = true;
        const vp = measure();
        const limits = scaleLimits(vp);
        commit(zoomCamera(camRef.current, 1.12, vp.width / 2, vp.height / 2, limits.min, limits.max));
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        interactedRef.current = true;
        const vp = measure();
        const limits = scaleLimits(vp);
        commit(zoomCamera(camRef.current, 1 / 1.12, vp.width / 2, vp.height / 2, limits.min, limits.max));
      } else if (event.key === "0") {
        event.preventDefault();
        reset();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [commit, measure, reset, stopCoast]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const vp = measure();
      if (!interactedRef.current) {
        commit(cameraOnHome(layoutRef.current.home, vp));
      } else {
        commit(camRef.current);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [commit, measure]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (coastRafRef.current) cancelAnimationFrame(coastRafRef.current);
    };
  }, []);

  return {
    viewportRef,
    worldRef,
    view,
    dragging,
    dragRef,
    reset,
  };
}
